import { createSlice } from '@reduxjs/toolkit';
import { filter, includes, map, uniqBy } from 'lodash';

import socket from '../../utils/socket';

/**
 * Initial state setup for the invoices slice, defining all the relevant properties
 * that manage the state of invoices throughout the application.
 */
const initialState = {
  isLoading: false,
  error: false,
  invoices: [],
  sortBy: ['clientId', 'date'],
  sortOrders: ['asc', 'desc'],
  filterOptions: {
    clients: [],
    years: []
  },
  filters: {
    clients: [],
    years: [],
    unassigned: false,
    inProgress: false,
    finished: false
  }
};

/**
 * Slice for invoices management with several reducers to handle the different
 * state changes related to invoices such as fetching invoices, setting individual invoice,
 * sorting and filtering invoices.
 */
const slice = createSlice({
  name: 'invoices',
  initialState,
  reducers: {
    // LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
      console.error(state.error);
    },

    getInvoices(state, action) {
      const { bills: invoices } = action.payload;
      const clients = uniqBy(invoices, (el) => el.clientId).map(extractClients);
      const years = uniqBy(invoices, (el) => el.year).map(extractYears);
      state.invoices = map(invoices, (el) => el);
      state.filterOptions.clients = clients;
      state.filterOptions.years = years;
      state.filters.years = years[0] ? [years[0]] : [];
      state.isLoading = false;
    },

    setInvoice(state, action) {
      const service = action.payload;
      const idx = state.invoices.findIndex((el) => el.id === service.id);
      if (idx >= 0) {
        state.invoices[idx] = { ...state.invoices[idx], ...service };
      }
    },

    // SORT
    sortByInvoices(state, action) {
      const idx = state.sortBy.findIndex((el) => el === action.payload);
      if (idx >= 0) {
        if (state.sortOrders[0] === 'desc') {
          state.sortBy.splice(0, 1);
          state.sortOrders.splice(0, 1);
        } else {
          state.sortOrders[0] = 'desc';
        }
      } else {
        state.sortBy[0] = action.payload;
        state.sortOrders[0] = 'asc';
      }
    },

    // FILTER
    filterInvoices(state, action) {
      state.filters = { ...state.filters, ...action.payload };
    },

    // RESET FILTER
    resetInvoicesFilters(state, action) {
      state.filters = initialState.filters;
      action.payload = initialState.filters;
    }
  }
});

// Export the reducer from the slice.
export default slice.reducer;

// Export actions from the slice.
export const { setInvoice, sortByInvoices, filterInvoices, resetInvoicesFilters } = slice.actions;

/**
 * Thunk function for fetching invoices. It utilizes websocket connections to fetch data
 * and handles loading and error states accordingly.
 */
export function getInvoices() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const result = await socket.get('/bills');
      dispatch(slice.actions.getInvoices(result));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

/**
 * Function to apply filters to the invoices data based on selected criteria.
 */
export function applyFilter(datas, filters) {
  if (filters.clients.length) {
    datas = filter(datas, (data) =>
      includes(
        filters.clients.map((c) => c.id),
        data.client_id
      )
    );
  }
  if (filters.years.length) {
    datas = filter(datas, (data) =>
      includes(
        filters.years.map((y) => y.id),
        data.year
      )
    );
  }
  return datas;
}

/**
 * Helper functions to extract unique client and year information from the invoices data.
 */
function extractClients(invoice) {
  return { id: invoice.clientId, code: invoice.clientCode, name: invoice.clientName };
}

function extractYears(invoice) {
  const year = invoice.year.toString(); // Ensure year is a string to meet UI component requirements
  return { id: invoice.year, name: year };
}
